/**
 * 広告画像エディタで利用可能な Web フォント一覧（Phase 29.3）
 *
 * - すべて Google Fonts で配信される
 * - admin-web/index.html で <link> 経由でロード
 * - Claude のレイアウト Pass プロンプトにも渡し、AI が用途に応じて選択できるようにする
 * - カスタムフォント（ユーザーアップロード）は `customFonts/{id}` Firestore コレクションで管理し、
 *   実行時に @font-face を動的注入する
 */
export interface AdFontOption {
  /** CSS font-family 値 */
  family: string;
  /** UI 表示用ラベル */
  label: string;
  /** 用途タグ — Claude が選択時のヒントに使う */
  category: 'sans' | 'serif' | 'display' | 'handwriting' | 'mono' | 'ud';
  /** 日本語対応か */
  japanese: boolean;
  /** 雰囲気タグ — Claude が雰囲気から選ぶためのキーワード */
  vibe: string;
}

export const AD_FONT_OPTIONS: AdFontOption[] = [
  // Sans-serif (Japanese)
  { family: 'Noto Sans JP', label: 'Noto Sans JP', category: 'sans', japanese: true, vibe: 'clean, modern, universal' },
  { family: 'M PLUS 1p', label: 'M PLUS 1p', category: 'sans', japanese: true, vibe: 'geometric, friendly' },
  { family: 'Zen Kaku Gothic New', label: 'Zen Kaku Gothic New', category: 'sans', japanese: true, vibe: 'modern, square, premium' },
  { family: 'Zen Maru Gothic', label: 'Zen Maru Gothic', category: 'sans', japanese: true, vibe: 'rounded, soft, friendly' },
  { family: 'Kosugi Maru', label: 'Kosugi Maru', category: 'sans', japanese: true, vibe: 'rounded, casual' },
  { family: 'Sawarabi Gothic', label: 'Sawarabi Gothic', category: 'sans', japanese: true, vibe: 'humanist, warm' },
  { family: 'BIZ UDPGothic', label: 'BIZ UDPGothic', category: 'ud', japanese: true, vibe: 'business, readable, accessible' },
  { family: 'Murecho', label: 'Murecho', category: 'sans', japanese: true, vibe: 'contemporary, neutral' },

  // Serif (Japanese)
  { family: 'Noto Serif JP', label: 'Noto Serif JP', category: 'serif', japanese: true, vibe: 'classic, elegant, editorial' },
  { family: 'Sawarabi Mincho', label: 'Sawarabi Mincho', category: 'serif', japanese: true, vibe: 'traditional, refined' },
  { family: 'Shippori Mincho', label: 'Shippori Mincho', category: 'serif', japanese: true, vibe: 'poster mincho, dramatic' },
  { family: 'Hina Mincho', label: 'Hina Mincho', category: 'serif', japanese: true, vibe: 'delicate, soft, feminine' },
  { family: 'Klee One', label: 'Klee One', category: 'serif', japanese: true, vibe: 'school textbook, hand-warmth' },
  { family: 'BIZ UDPMincho', label: 'BIZ UDPMincho', category: 'ud', japanese: true, vibe: 'business serif, accessible' },

  // Display / Handwriting (Japanese)
  { family: 'Yusei Magic', label: 'Yusei Magic', category: 'handwriting', japanese: true, vibe: 'handwritten, casual, playful' },
  { family: 'RocknRoll One', label: 'RocknRoll One', category: 'display', japanese: true, vibe: 'chunky, bold, impactful' },
  { family: 'Reggae One', label: 'Reggae One', category: 'display', japanese: true, vibe: 'graffiti, energetic, urban' },
  { family: 'Stick', label: 'Stick', category: 'display', japanese: true, vibe: 'thin, technical, minimal' },
  { family: 'DotGothic16', label: 'DotGothic16', category: 'display', japanese: true, vibe: 'pixel, retro, 8bit' },

  // Latin
  { family: 'Inter', label: 'Inter', category: 'sans', japanese: false, vibe: 'clean, tech, contemporary' },
  { family: 'Roboto', label: 'Roboto', category: 'sans', japanese: false, vibe: 'neutral, screen-optimized' },
  { family: 'Playfair Display', label: 'Playfair Display', category: 'serif', japanese: false, vibe: 'high-contrast serif, luxury' },
  { family: 'Montserrat', label: 'Montserrat', category: 'sans', japanese: false, vibe: 'geometric, urban, posters' },
  { family: 'Bebas Neue', label: 'Bebas Neue', category: 'display', japanese: false, vibe: 'tall condensed, headline' },
  { family: 'Oswald', label: 'Oswald', category: 'display', japanese: false, vibe: 'condensed, editorial' },
  { family: 'DM Sans', label: 'DM Sans', category: 'sans', japanese: false, vibe: 'low-contrast, friendly' },
  { family: 'JetBrains Mono', label: 'JetBrains Mono', category: 'mono', japanese: false, vibe: 'monospace, technical' },
];

/** Claude プロンプト用の人間可読サマリー */
export function summarizeAdFonts(): string {
  return AD_FONT_OPTIONS.map(
    (f) =>
      `- "${f.family}" [${f.category}${f.japanese ? '/JP' : ''}]: ${f.vibe}`,
  ).join('\n');
}

/** UI で使う family リスト（プレーン） */
export const AD_FONT_FAMILIES: string[] = AD_FONT_OPTIONS.map((f) => f.family);
